import { override } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import DiscussionPageResolver from 'flarum/forum/resolvers/DiscussionPageResolver';
import speciesSlug from './utils/speciesSlug';
import titleNamesSpecies from './utils/titleNamesSpecies';
/**
 * The one species' canonicalName for a discussion tagged with exactly one
 * species (null otherwise - none, or a multi-species discussion, where no
 * single name describes it). Mirrored server-side by
 * Content\DiscussionSpecies, which applies the same rule to the
 * prerendered <title>/canonical URL - keep the two in sync.
 */
export function singleSpeciesName(discussion) {
  const species = discussion.species ? discussion.species() : undefined;
  if (!species || species.length !== 1) return null;
  return species[0].canonicalName || null;
}

/**
 * SEO: a discussion tagged with exactly one species gets that species in
 * its URL (/d/1038098-elle-brise-son-voile/catathelasma-imperiale) and its
 * <title> ("Catathelasma imperiale - Elle brise son voile", same
 * "species - title" order as OpenGraphTags' og:title).
 *
 * The species slug reuses core's own optional /d/:id/:near segment rather
 * than a new route: a non-numeric `near` already falls back to the first
 * post both server-side (Content\Discussion's intval()) and in
 * DiscussionPage (parseInt -> NaN -> first post). A URL pointing at a
 * specific post keeps core's plain /d/:id/:near form, since that segment
 * can only hold one of the two.
 */
export default function addDiscussionSpeciesUrl() {
  const originalDiscussionRoute = app.route.discussion;
  app.route.discussion = (discussion, near) => {
    const name = !near || near === 1 ? singleSpeciesName(discussion) : null;
    const slug = speciesSlug(name);
    return slug ? app.route('discussion.near', {
      id: discussion.slug(),
      near: slug
    }) : originalDiscussionRoute(discussion, near);
  };
  override(DiscussionPage.prototype, 'show', function (original, discussion, preloadedPosts) {
    original(discussion, preloadedPosts);
    const name = singleSpeciesName(discussion);

    // Not in front of a title that already names it (see titleNamesSpecies).
    if (name && !titleNamesSpecies(discussion.title(), name)) app.setTitle(`${name} - ${discussion.title()}`);
  });

  // onmatch() treats any truthy `near` as a post number to scroll to when
  // re-routing within the discussion already open - the species slug isn't
  // one, so it's dropped here (-> core's own "first post" default).
  override(DiscussionPageResolver.prototype, 'onmatch', function (original, args, requestedPath, route) {
    if (args.near && args.near !== 'reply' && isNaN(parseInt(args.near))) {
      args = {
        ...args,
        near: undefined
      };
    }
    return original(args, requestedPath, route);
  });
}